import React from 'react';
import PropTypes from 'prop-types';
import {parse, stringify} from 'query-string';
import ReactDOM from 'react-dom';
import update from 'immutability-helper';
import {DropTarget, DragDropContext} from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import {withRouter} from 'common/utils/with-router';
import StoryboardNavButton from 'storyboard/components/storyboard-nav-button';
import StoryboardAddChapterButton from 'storyboard/components/storyboard-add-chapter-button';
import * as DragItemType from 'storyboard/constants/drag-item-type';
import {sort} from 'common/utils/array-util';
import {getColorBasedOnContrast} from 'common/utils/color-util';
import {getLinearGradient} from 'common/utils/style-util';
import './index.scss';

const chapterTarget = {
  drop(props, monitor, component) {
    props.positionChapters(
      component.state.chapters.map((chapter) => chapter.id),
    );
  },
};

const collectDrop = (connect) => ({
  connectDropTarget: connect.dropTarget(),
});

class StoryboardNav extends React.Component {
  static defaultProps = {
    openModal: () => {},
  };

  static propTypes = {
    connectDropTarget: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    enums: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    openModal: PropTypes.func,
  };

  constructor() {
    super();
    this.state = {
      buttonSize: 0,
      chapters: [],
    };
    this.findChapter = this.findChapter.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.moveChapter = this.moveChapter.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.setChapters(this.props.data.chapters);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.setButtonSize(this.props.data, this.props.enums);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setChapters(nextProps.data.chapters);
  }

  componentDidUpdate(prevProps) {
    this.setButtonSize(this.props.data, this.props.enums);
    this.navigateToAddedChapter(
      this.state.chapters,
      prevProps.data.chapters,
      this.props.location,
      this.props.navigate,
    );
    this.navigateAwayFromDeletedChapter(
      this.state.chapters,
      prevProps.data.chapters,
      this.props.location,
      this.props.navigate,
    );
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  getClassName(isVertical) {
    return isVertical ? 'StoryboardNav--vertical' : 'StoryboardNav--horizontal';
  }

  getFontSize(isEditable, configuration, enums) {
    if (isEditable && configuration && configuration.theme) {
      const {NavPositionEnum} = enums;
      switch (configuration.theme.navPosition) {
        case NavPositionEnum.LEFT.id:
        case NavPositionEnum.RIGHT.id:
          return '0.75vw';
        default:
          return '0.5vw';
      }
    }
    return '1.5vmin';
  }

  getOrientation(data, enums) {
    const {theme} = data.configuration;
    const {NavPositionEnum} = enums;
    switch (theme.navPosition) {
      case NavPositionEnum.LEFT.id:
      case NavPositionEnum.RIGHT.id:
        return true;
      default:
        return false;
    }
  }

  getTextColor(configuration) {
    if (configuration && configuration.theme) {
      return getColorBasedOnContrast(configuration.theme.navColors[0]);
    }
  }

  setButtonSize(data, enums) {
    const {theme} = data.configuration;
    const {NavPositionEnum} = enums;
    if (this.node) {
      let buttonSize = this.node.clientHeight;
      if (
        theme.navPosition === NavPositionEnum.LEFT.id ||
        theme.navPosition === NavPositionEnum.RIGHT.id
      ) {
        buttonSize = this.node.clientWidth;
      }
      if (buttonSize !== this.state.buttonSize) {
        this.setState({
          buttonSize,
        });
      }
    }
  }

  setChapters(chapters) {
    this.setState({
      chapters: sort.ascending([].concat(chapters), 'position'),
    });
  }

  findChapter(id) {
    const {chapters} = this.state;
    const currentChapter = chapters
      .filter((chapter) => chapter.id === id)
      .shift();
    return {
      chapter: currentChapter,
      index: chapters.indexOf(currentChapter),
    };
  }

  isActive(chapter, chapterId) {
    return chapter.id === parseInt(chapterId, 10);
  }

  moveChapter(id, atIndex) {
    const {chapter, index} = this.findChapter(id);
    this.setState(
      update(this.state, {
        chapters: {
          $splice: [
            [index, 1],
            [atIndex, 0, chapter],
          ],
        },
      }),
    );
  }

  navigateAwayFromDeletedChapter(chapters, prevChapters, location, navigate) {
    if (chapters.length < prevChapters.length) {
      // Find chapter corresponding to url query:
      const {chapterId} = parse(location.search);
      const chapterFound = chapters.find((chapter) =>
        parseInt(chapterId, 10) === chapter.id ? chapter : null,
      );

      // Chapter on url not found in memory, so
      // Navigate to first chapter (if one exists):
      if (!chapterFound && chapters.length) {
        navigate({
          pathname: location.pathname,
          search: stringify({
            chapterId: chapters[0].id,
          }),
        });
      }

      // No chapters found. Clear the url query.
      // This will trigger storyboard zero state:
      if (!chapterFound && !chapters.length) {
        navigate({
          pathname: location.pathname,
          search: stringify({}),
        });
      }
    }
  }

  navigateToAddedChapter(chapters, prevChapters, location, navigate) {
    if (chapters.length > prevChapters.length) {
      const addedChapter = chapters.find((chapter) =>
        chapter && chapter.justAdded ? chapter : null,
      );
      if (addedChapter) {
        navigate({
          pathname: location.pathname,
          search: stringify({
            chapterId: addedChapter.id,
          }),
        });
      }
    }
  }

  render() {
    const isVertical = this.getOrientation(this.props.data, this.props.enums);
    const className = this.getClassName(isVertical);
    const fontSize = this.getFontSize(
      this.props.data.isEditable,
      this.props.data.configuration,
      this.props.enums,
    );
    const textColor = this.getTextColor(this.props.data.configuration);
    return this.props.connectDropTarget(
      <div
        className={className}
        ref={(node) => {
          this.node = node;
        }}
        style={{
          background: getLinearGradient(
            this.props.data.configuration.theme.navColors,
          ),
        }}>
        {this.state.chapters.map((chapter) => {
          const isActive = this.isActive(
            chapter,
            parse(this.props.location.search).chapterId,
          );
          return (
            <StoryboardNavButton
              active={isActive}
              chapter={chapter}
              data={this.props.data}
              enums={this.props.enums}
              findChapter={this.findChapter}
              fontSize={fontSize}
              id={chapter.id}
              key={chapter.id}
              location={this.props.location}
              moveChapter={this.moveChapter}
              openModal={this.props.openModal}
              textColor={textColor}
              width={this.state.buttonSize}
            />
          );
        })}
        {this.props.data.isEditable ? (
          <StoryboardAddChapterButton
            enums={this.props.enums}
            fontSize={fontSize}
            openModal={this.props.openModal}
            textColor={textColor}
            width={this.state.buttonSize}
          />
        ) : null}
      </div>,
    );
  }

  handleResize() {
    this.setButtonSize(this.props.data, this.props.enums);
  }
}

// Intermediate drop target component
const DropTargetComponent = DropTarget(
  DragItemType.CHAPTER,
  chapterTarget,
  collectDrop,
)(StoryboardNav);

// Intermediate drag drop context component
const DragDropContextComponent =
  DragDropContext(HTML5Backend)(DropTargetComponent);

// Export default with router
export default withRouter(DragDropContextComponent);
